import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError, download } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useMeta, useMetaRefresh } from "../lib/meta";
import type { Category, Preferences, RewardCurrency } from "../lib/types";
import { describeLogin } from "../lib/format";
import { ErrorBanner, Modal, PageHeader, Panel, Spinner } from "../components/ui";
import { ListEditor } from "../components/ListEditor";
import { TemplatesTab } from "../components/TemplatesTab";
import { MerchantRulesTab } from "../components/MerchantRulesTab";
import { PeopleTab } from "../components/PeopleTab";

type Tab = "categories" | "household" | "templates" | "merchants" | "preferences" | "programmes" | "data" | "account";

const TABS: { key: Tab; label: string }[] = [
  { key: "categories", label: "Categories" },
  { key: "templates", label: "Card templates" },
  { key: "household", label: "Household" },
  { key: "merchants", label: "Merchant rules" },
  { key: "preferences", label: "Preferences" },
  { key: "programmes", label: "Reward programmes" },
  { key: "data", label: "Backup" },
  { key: "account", label: "Account" },
];

export default function SettingsPage() {
  // A link can open a tab directly: /settings?tab=templates.
  const [params] = useSearchParams();
  const asked = params.get("tab");
  const [tab, setTab] = useState<Tab>(TABS.some((t) => t.key === asked) ? (asked as Tab) : "categories");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Categories, pick-lists, thresholds and valuations — everything the app uses is yours to change."
      />

      <div className="mb-6 flex flex-wrap gap-1 border-b border-ink-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setError(null); setNotice(null); }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "border-accent-600 text-accent-800"
                : "border-transparent text-ink-500 hover:text-ink-800"
            }`}
            aria-current={tab === t.key ? "page" : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {tab === "categories" && <CategoriesTab onError={setError} onNotice={setNotice} />}
      {tab === "templates" && <TemplatesTab onError={setError} onNotice={setNotice} />}
      {tab === "household" && <PeopleTab onError={setError} onNotice={setNotice} />}
      {tab === "merchants" && <MerchantRulesTab onError={setError} onNotice={setNotice} />}
      {tab === "preferences" && <PreferencesTab onError={setError} onNotice={setNotice} />}
      {tab === "programmes" && <ProgrammesTab onError={setError} />}
      {tab === "data" && <DataTab onError={setError} onNotice={setNotice} />}
      {tab === "account" && <AccountTab onError={setError} onNotice={setNotice} />}
    </>
  );
}

/* -------------------------------------------------------------- categories */

function CategoriesTab({
  onError,
  onNotice,
}: {
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const refreshMeta = useMetaRefresh();
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [editing, setEditing] = useState<Category | null>(null);
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ categories: Category[] }>("/settings/categories");
      setCategories(r.categories);
      await refreshMeta();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load categories");
    }
  }, [onError, refreshMeta]);

  useEffect(() => { void load(); }, [load]);

  async function toggleArchive(c: Category) {
    try {
      await api.put(`/settings/categories/${c.id}`, { isArchived: !c.isArchived });
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update the category");
    }
  }

  async function remove(c: Category) {
    if (!confirm(`Delete "${c.label}"?`)) return;
    try {
      await api.del(`/settings/categories/${c.id}`);
      onNotice(`Deleted "${c.label}".`);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the category");
    }
  }

  async function move(index: number, direction: -1 | 1) {
    if (!categories) return;
    const next = [...categories];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCategories(next);
    try {
      await api.put("/settings/categories", { ids: next.map((c) => c.id) });
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not reorder");
    }
  }

  if (!categories) return <Spinner />;

  const visible = showArchived ? categories : categories.filter((c) => !c.isArchived);

  return (
    <>
      <Panel
        title="Spend categories"
        subtitle="Used by transactions, earn rules and the optimiser. Order here is the order in every picker."
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-ink-600">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setAdding(true)}>
              Add category
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-200">
                <th className="th">Order</th>
                <th className="th">Category</th>
                {/* The key and the counts are reference, not actions. On a
                    phone they would sit between the category and the only two
                    things you can do to it, so they wait for a wider screen. */}
                <th className="th hidden sm:table-cell">Key</th>
                <th className="th hidden text-right sm:table-cell">Transactions</th>
                <th className="th hidden text-right sm:table-cell">Earn rules</th>
                <th className="th"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {visible.map((c, index) => (
                <tr key={c.id} className={c.isArchived ? "opacity-55" : undefined}>
                  <td className="td">
                    <div className="flex gap-1">
                      <button
                        className="btn-ghost px-1.5 py-0.5 text-xs"
                        onClick={() => move(index, -1)}
                        disabled={index === 0}
                        aria-label={`Move ${c.label} up`}
                      >
                        ↑
                      </button>
                      <button
                        className="btn-ghost px-1.5 py-0.5 text-xs"
                        onClick={() => move(index, 1)}
                        disabled={index === visible.length - 1}
                        aria-label={`Move ${c.label} down`}
                      >
                        ↓
                      </button>
                    </div>
                  </td>
                  <td className="td">
                    <span className="mr-2" aria-hidden>{c.icon}</span>
                    <span className="font-medium text-ink-900">{c.label}</span>
                    {c.isArchived && <span className="ml-2 text-xs text-ink-500">archived</span>}
                  </td>
                  <td className="td hidden font-mono text-xs text-ink-500 sm:table-cell">{c.key}</td>
                  <td className="td tnum hidden text-right text-ink-600 sm:table-cell">{c.transactionCount}</td>
                  <td className="td tnum hidden text-right text-ink-600 sm:table-cell">{c.ruleCount}</td>
                  <td className="td whitespace-nowrap text-right">
                    <button className="text-xs text-accent-700 underline" onClick={() => setEditing(c)}>Rename</button>
                    <button className="ml-3 text-xs text-ink-600 underline" onClick={() => toggleArchive(c)}>
                      {c.isArchived ? "Restore" : "Archive"}
                    </button>
                    {c.transactionCount === 0 && c.ruleCount === 0 && (
                      <button className="ml-3 text-xs text-red-700 underline" onClick={() => remove(c)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 space-y-1 text-xs text-ink-500">
          <p>
            <strong>Renaming is always safe.</strong> Transactions and earn rules reference the key, not the
            name, so the key never changes once a category exists.
          </p>
          <p>
            <strong>Archive rather than delete</strong> a category you've used — it disappears from the pickers
            while your history stays intact. Delete is only offered when nothing references it.
          </p>
          <p>The key is what the CSV importer matches on.</p>
        </div>
      </Panel>

      <CategoryModal
        open={adding || editing != null}
        category={editing}
        onClose={() => { setAdding(false); setEditing(null); }}
        onSaved={() => { setAdding(false); setEditing(null); void load(); }}
      />
    </>
  );
}

function CategoryModal({
  open,
  category,
  onClose,
  onSaved,
}: {
  open: boolean;
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLabel(category?.label ?? "");
    setIcon(category?.icon ?? "");
    setError(null);
  }, [category, open]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = { label: label.trim(), icon: icon.trim() || "•" };
      if (category) await api.put(`/settings/categories/${category.id}`, payload);
      else await api.post("/settings/categories", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the category");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={category ? "Rename category" : "Add category"}>
      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="label" htmlFor="cat-label">Name</label>
          <input
            id="cat-label"
            className="input"
            required
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Pet Care"
          />
        </div>
        <div>
          <label className="label" htmlFor="cat-icon">Icon (optional)</label>
          <input
            id="cat-icon"
            className="input"
            maxLength={8}
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            placeholder="🐾"
          />
        </div>
        {category && (
          <p className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-xs text-ink-600">
            Key <code className="font-mono">{category.key}</code> stays as it is, so your{" "}
            {category.transactionCount} transaction(s) and {category.ruleCount} earn rule(s) keep working.
          </p>
        )}
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------- preferences */

function PreferencesTab({
  onError,
  onNotice,
}: {
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const meta = useMeta();
  const refreshMeta = useMetaRefresh();
  const [form, setForm] = useState<Preferences | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (meta && !form) setForm(meta.preferences);
  }, [meta, form]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    setBusy(true);
    try {
      await api.put("/settings/preferences", form);
      await refreshMeta();
      onNotice("Preferences saved.");
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not save preferences");
    } finally {
      setBusy(false);
    }
  }

  if (!form) return <Spinner />;

  return (
    <form onSubmit={save} className="space-y-6">
      <Panel title="Thresholds" subtitle="The numbers the dashboard and points pages are built around">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="label" htmlFor="p-expiry">“Expiring soon” window (days)</label>
            <input
              id="p-expiry"
              className="input tnum"
              type="number"
              min={1}
              max={3650}
              value={form.expiringSoonDays}
              onChange={(e) => setForm({ ...form, expiringSoonDays: Number(e.target.value) })}
            />
            <p className="mt-1 text-xs text-ink-500">How far ahead an expiry warning looks.</p>
          </div>
          <div>
            <label className="label" htmlFor="p-months">Months on the trend charts</label>
            <input
              id="p-months"
              className="input tnum"
              type="number"
              min={3}
              max={24}
              value={form.dashboardMonths}
              onChange={(e) => setForm({ ...form, dashboardMonths: Number(e.target.value) })}
            />
            <p className="mt-1 text-xs text-ink-500">Between 3 and 24.</p>
          </div>
          <div>
            <label className="label" htmlFor="p-miles">Milestones on the dashboard</label>
            <input
              id="p-miles"
              className="input tnum"
              type="number"
              min={1}
              max={50}
              value={form.dashboardMilestones}
              onChange={(e) => setForm({ ...form, dashboardMilestones: Number(e.target.value) })}
            />
            <p className="mt-1 text-xs text-ink-500">How many to list before “view all”.</p>
          </div>
          <div>
            <label className="label" htmlFor="p-forex">Default forex markup (%)</label>
            <input
              id="p-forex"
              className="input tnum"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={form.defaultForexMarkupPct}
              onChange={(e) => setForm({ ...form, defaultForexMarkupPct: Number(e.target.value) })}
            />
            <p className="mt-1 text-xs text-ink-500">Pre-filled when you add a card by hand.</p>
          </div>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
            checked={form.optimiserUsesBestValue}
            onChange={(e) => setForm({ ...form, optimiserUsesBestValue: e.target.checked })}
          />
          Value points at my best redemption rate in the optimiser by default
        </label>

        <label className="mt-3 flex items-start gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
            checked={form.poolAcrossPeople}
            onChange={(e) => setForm({ ...form, poolAcrossPeople: e.target.checked })}
          />
          <span>
            Pool the household&apos;s points when planning a redemption
            <span className="block text-xs text-ink-500">
              Off means a plan only draws on cards belonging to whoever owns the loyalty account. A card
              can override this either way.
            </span>
          </span>
        </label>
      </Panel>

      <Panel title="Optimiser shortcuts" subtitle="The quick-amount buttons above the ranking">
        <ListEditor
          values={form.quickAmounts.map(String)}
          numeric
          placeholder="e.g. 75000"
          onChange={(next) =>
            setForm({
              ...form,
              quickAmounts: next.map(Number).filter((n) => Number.isFinite(n) && n > 0),
            })
          }
        />
      </Panel>

      <Panel title="Redemption types" subtitle="The options offered when logging a redemption">
        <ListEditor
          values={form.redemptionTypes}
          minimum={1}
          placeholder="e.g. Airline transfer"
          onChange={(next) => setForm({ ...form, redemptionTypes: next })}
        />
      </Panel>

      <Panel title="Card networks" subtitle="The options offered when adding or editing a card">
        <ListEditor
          values={form.cardNetworks}
          minimum={1}
          placeholder="e.g. DISCOVER"
          onChange={(next) => setForm({ ...form, cardNetworks: next })}
        />
        <p className="mt-2 text-xs text-ink-500">
          Removing a network doesn't change cards already using it — it just stops appearing in the picker.
        </p>
      </Panel>

      <div className="flex justify-end">
        <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save preferences"}</button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------- programmes */

function ProgrammesTab({ onError }: { onError: (m: string) => void }) {
  const [currencies, setCurrencies] = useState<RewardCurrency[] | null>(null);
  const [modal, setModal] = useState<{ open: boolean; currency: RewardCurrency | null }>({ open: false, currency: null });

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ currencies: RewardCurrency[] }>("/points/currencies");
      setCurrencies(r.currencies);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load programmes");
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  async function remove(c: RewardCurrency) {
    if ((c._count?.cards ?? 0) > 0) {
      onError(`${c.name} is used by ${c._count?.cards} card(s). Move those cards to another programme first.`);
      return;
    }
    if (!confirm(`Delete "${c.name}"? Its redemption history will be deleted too.`)) return;
    try {
      await api.del(`/points/currencies/${c.id}`);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the programme");
    }
  }

  if (!currencies) return <Spinner />;

  return (
    <>
      <Panel
        title="Reward programmes"
        subtitle="What you value a point at drives every rupee figure in the app"
        actions={
          <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setModal({ open: true, currency: null })}>
            Add programme
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-200">
                <th className="th">Programme</th>
                <th className="th text-right">Everyday value</th>
                <th className="th text-right">Best value</th>
                <th className="th text-right">Expiry</th>
                <th className="th text-right">Cards</th>
                <th className="th"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {currencies.map((c) => (
                <tr key={c.id}>
                  <td className="td">
                    <span className="font-medium text-ink-900">{c.name}</span>
                    {c.notes && <span className="block text-xs text-ink-500">{c.notes}</span>}
                  </td>
                  <td className="td tnum text-right">₹{c.valuePerPoint}</td>
                  <td className="td tnum text-right text-ink-500">{c.bestValuePerPoint ? `₹${c.bestValuePerPoint}` : "—"}</td>
                  <td className="td tnum text-right text-ink-500">{c.expiryMonths ? `${c.expiryMonths} months` : "Never"}</td>
                  <td className="td tnum text-right text-ink-500">{c._count?.cards ?? 0}</td>
                  <td className="td whitespace-nowrap text-right">
                    <button className="text-xs text-accent-700 underline" onClick={() => setModal({ open: true, currency: c })}>
                      Edit
                    </button>
                    <button className="ml-3 text-xs text-red-700 underline" onClick={() => remove(c)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-ink-500">
          Changing a valuation updates every reward figure instantly — points are stored as points, and only
          converted to rupees when displayed.
        </p>
      </Panel>

      <CurrencyModal
        state={modal}
        onClose={() => setModal({ open: false, currency: null })}
        onSaved={() => { setModal({ open: false, currency: null }); void load(); }}
      />
    </>
  );
}

function CurrencyModal({
  state,
  onClose,
  onSaved,
}: {
  state: { open: boolean; currency: RewardCurrency | null };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: "", valuePerPoint: "1", bestValuePerPoint: "", expiryMonths: "", isCashback: false, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const c = state.currency;
    setForm({
      name: c?.name ?? "",
      valuePerPoint: String(c?.valuePerPoint ?? 1),
      bestValuePerPoint: c?.bestValuePerPoint != null ? String(c.bestValuePerPoint) : "",
      expiryMonths: c?.expiryMonths != null ? String(c.expiryMonths) : "",
      isCashback: c?.isCashback ?? false,
      notes: c?.notes ?? "",
    });
  }, [state.currency, state.open]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      valuePerPoint: Number(form.valuePerPoint) || 0,
      bestValuePerPoint: form.bestValuePerPoint ? Number(form.bestValuePerPoint) : null,
      expiryMonths: form.expiryMonths ? Number(form.expiryMonths) : null,
      isCashback: form.isCashback,
      notes: form.notes.trim() || null,
    };
    try {
      if (state.currency) await api.put(`/points/currencies/${state.currency.id}`, payload);
      else await api.post("/points/currencies", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the programme");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={state.open} onClose={onClose} title={state.currency ? "Edit programme" : "Add reward programme"}>
      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="label" htmlFor="cu-name">Name</label>
          <input id="cu-name" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. HDFC Reward Points" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="cu-value">Everyday value per point (₹)</label>
            <input id="cu-value" className="input tnum" type="number" min={0} step="0.01" required value={form.valuePerPoint} onChange={(e) => setForm({ ...form, valuePerPoint: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cu-best">Best-case value per point (₹)</label>
            <input id="cu-best" className="input tnum" type="number" min={0} step="0.01" value={form.bestValuePerPoint} onChange={(e) => setForm({ ...form, bestValuePerPoint: e.target.value })} placeholder="Optional" />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="cu-expiry">Points expire after (months)</label>
          <input id="cu-expiry" className="input tnum" type="number" min={1} value={form.expiryMonths} onChange={(e) => setForm({ ...form, expiryMonths: e.target.value })} placeholder="Leave empty if they never expire" />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
            checked={form.isCashback}
            onChange={(e) => setForm({ ...form, isCashback: e.target.checked })}
          />
          This is cashback (1 point = ₹1)
        </label>

        <div>
          <label className="label" htmlFor="cu-notes">Notes</label>
          <input id="cu-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. ₹1/pt on SmartBuy, ₹0.30 on the catalogue" />
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------------- data */

function DataTab({ onError, onNotice }: { onError: (m: string) => void; onNotice: (m: string) => void }) {
  return (
    <Panel title="Backup & export" subtitle="Your data, in formats you can keep">
      <div className="flex flex-wrap gap-3">
        <button
          className="btn-secondary"
          onClick={async () => {
            try {
              await download("/data/export", `ccpoints-backup-${new Date().toISOString().slice(0, 10)}.json`);
              onNotice("Backup downloaded.");
            } catch {
              onError("Could not download the backup");
            }
          }}
        >
          Download full backup (JSON)
        </button>
        <button
          className="btn-secondary"
          onClick={async () => {
            try {
              await download("/data/export.csv", "transactions.csv");
              onNotice("Transactions exported.");
            } catch {
              onError("Could not export transactions");
            }
          }}
        >
          Export transactions (CSV)
        </button>
      </div>
      <p className="mt-3 text-xs text-ink-500">
        The JSON backup contains every card, add-on card, rule, transaction, milestone, redemption,
        category, person and preference on this account.
      </p>
    </Panel>
  );
}

/* ----------------------------------------------------------------- account */

function AccountTab({ onError, onNotice }: { onError: (m: string) => void; onNotice: (m: string) => void }) {
  const { user, signOut } = useAuth();
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ? describeLogin(user.email) : "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/settings/account", { name: name.trim() || null, email: email.trim() });
      onNotice(
        email.trim().toLowerCase() !== (user?.email ? describeLogin(user.email) : "").toLowerCase()
          ? "Profile saved. Sign in with your new email or number next time."
          : "Profile saved.",
      );
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not save your profile");
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    setSavingPassword(true);
    try {
      await api.put("/settings/password", { currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      onNotice("Password changed. Existing sign-ins stay valid until they expire.");
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not change your password");
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <div className="space-y-6">
      <Panel title="Profile">
        <form onSubmit={saveProfile} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="a-name">Name</label>
              <input id="a-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="a-email">Email or mobile number</label>
              <input id="a-email" className="input" type="text" required autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} />
              <p className="mt-1 text-xs text-ink-500">What you sign in with.</p>
            </div>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" disabled={savingProfile}>{savingProfile ? "Saving…" : "Save profile"}</button>
          </div>
        </form>
      </Panel>

      <Panel title="Change password">
        <form onSubmit={savePassword} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="a-current">Current password</label>
              <input
                id="a-current"
                className="input"
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="a-new">New password</label>
              <input
                id="a-new"
                className="input"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" disabled={savingPassword}>
              {savingPassword ? "Saving…" : "Change password"}
            </button>
          </div>
        </form>
      </Panel>

      <Panel title="This deployment">
        <p className="text-xs text-ink-500">
          To stop anyone else signing up, set <code className="rounded bg-ink-100 px-1">DISABLE_SIGNUP=true</code>{" "}
          in the server's environment and restart it.
        </p>
        <button className="btn-secondary mt-4" onClick={signOut}>Sign out</button>
      </Panel>
    </div>
  );
}
